/** QuickRouter — Écriture des Gerber RS-274X (X2 de base) d'une carte 2, 4 ou 6 couches.
 *
 * Format 4.6 en millimètres. Les pastilles rondes, rectangulaires et oblongues droites
 * (rotation multiple de 90°) sont « flashées » avec une ouverture C/R/O ; les rectangles
 * arrondis et les pastilles tournées d'un angle quelconque deviennent des régions G36/G37.
 *
 * Zones de cuivre : région sombre (%LPD), puis dégagements clairs (%LPC) autour du cuivre
 * des AUTRES nets, des trous non métallisés et des zones interdites des empreintes, puis
 * le cuivre normal est redessiné en sombre. Les pastilles du net de la zone y sont reliées
 * en plein (pas de pastilles thermiques en v0.1 : soudure à la main plus lente sur GND).
 */
import { worldPads, rectCorners, rotate, placePoint, padShape, shapeDist, courtyardPoly, polyArea, polyBox } from '../geom.js';
import { padNetMap } from '../netlist.js';
import { copperLayers, layerNumber, isOuter } from '../layers.js';
import { strokeText, strokeWidth } from './font.js';

export const MASK_MARGIN = 0.05;
const SILK_PAD_GAP = 0.15;

// ── Écrivain Gerber ────────────────────────────────────────────

const coord = (v) => {
  if (!Number.isFinite(v)) throw new Error(`coordonnée Gerber invalide : ${v}`);
  return String(Math.round(v * 1e6));
};
const size = (v) => {
  if (!Number.isFinite(v) || v <= 0) throw new Error(`dimension d'ouverture invalide : ${v}`);
  return v.toFixed(4);
};
const xy = (p) => `X${coord(p[0])}Y${coord(p[1])}`;

export class GerberWriter {
  constructor(fileFunction, polarity = 'Positive') {
    this.fileFunction = fileFunction;
    this.polarity = polarity;
    this.apertures = new Map(); // définition → numéro D
    this.body = [];
    this.current = null;
    this.lp = 'D';
  }

  /** Sélectionne (et déclare au besoin) une ouverture : « C,0.2500 », « R,1.0000X0.6000 »… */
  use(def) {
    let code = this.apertures.get(def);
    if (!code) { code = 10 + this.apertures.size; this.apertures.set(def, code); }
    if (this.current !== code) { this.body.push(`D${code}*`); this.current = code; }
  }

  circle(d) { return `C,${size(d)}`; }

  flash(def, p) { this.use(def); this.body.push(`${xy(p)}D03*`); }

  stroke(width, pts, closed = false) {
    if (pts.length < 2) return;
    this.use(this.circle(width));
    this.body.push(`${xy(pts[0])}D02*`);
    for (let i = 1; i < pts.length; i++) this.body.push(`${xy(pts[i])}D01*`);
    if (closed) this.body.push(`${xy(pts[0])}D01*`);
  }

  region(pts) {
    if (pts.length < 3) return;
    this.body.push('G36*', `${xy(pts[0])}D02*`);
    for (let i = 1; i < pts.length; i++) this.body.push(`${xy(pts[i])}D01*`);
    this.body.push(`${xy(pts[0])}D01*`, 'G37*');
  }

  /** Polarité des objets suivants : 'D' (sombre, ajoute) ou 'C' (claire, efface). */
  dark(on = true) {
    const lp = on ? 'D' : 'C';
    if (lp !== this.lp) { this.body.push(`%LP${lp}*%`); this.lp = lp; }
  }

  toString() {
    const head = [
      '%FSLAX46Y46*%',
      '%MOMM*%',
      '%TF.GenerationSoftware,QuickApps,QuickRouter*%',
      `%TF.CreationDate,${new Date().toISOString().slice(0, 19)}*%`,
      `%TF.FileFunction,${this.fileFunction}*%`,
      `%TF.FilePolarity,${this.polarity}*%`,
      ...[...this.apertures.entries()].map(([def, code]) => `%ADD${code}${def}*%`),
      '%LPD*%',
      'G01*',
    ];
    return `${[...head, ...this.body, 'M02*'].join('\n')}\n`;
  }
}

// ── Formes ─────────────────────────────────────────────────────

function capsulePoly(a, b, r, n = 10) {
  const ang = Math.atan2(b[1] - a[1], b[0] - a[0]);
  const pts = [];
  // Demi-cercle autour de b (côté sortant), puis autour de a.
  for (let k = 0; k <= n; k++) {
    const t = ang - Math.PI / 2 + (Math.PI * k) / n;
    pts.push([b[0] + Math.cos(t) * r, b[1] + Math.sin(t) * r]);
  }
  for (let k = 0; k <= n; k++) {
    const t = ang + Math.PI / 2 + (Math.PI * k) / n;
    pts.push([a[0] + Math.cos(t) * r, a[1] + Math.sin(t) * r]);
  }
  return pts;
}

function roundRectPoly(c, [w, h], rot, grow, n = 4) {
  const hw = w / 2 + grow, hh = h / 2 + grow;
  const r = Math.min(hw, hh, Math.min(w, h) * 0.25 + grow);
  const pts = [];
  const corners = [[hw - r, -hh + r, -90], [hw - r, hh - r, 0], [-hw + r, hh - r, 90], [-hw + r, -hh + r, 180]];
  for (const [cx, cy, a0] of corners) {
    for (let k = 0; k <= n; k++) {
      const a = ((a0 + (90 * k) / n) * Math.PI) / 180;
      const p = rotate([cx + r * Math.cos(a), cy + r * Math.sin(a)], rot);
      pts.push([c[0] + p[0], c[1] + p[1]]);
    }
  }
  return pts;
}

const norm360 = (a) => ((a % 360) + 360) % 360;
const isRight = (a) => Math.abs(norm360(a) % 90) < 1e-6 || Math.abs((norm360(a) % 90) - 90) < 1e-6;
const isQuarter = (a) => { const m = norm360(a); return Math.abs(m - 90) < 1e-6 || Math.abs(m - 270) < 1e-6; };

/** Dessine une pastille (grow = marge ajoutée : masque, dégagement de zone). */
export function drawPad(g, pad, grow = 0) {
  const [w0, h0] = pad.size;
  const swap = isQuarter(pad.rot);
  const w = (swap ? h0 : w0) + 2 * grow, h = (swap ? w0 : h0) + 2 * grow;
  if (pad.shape === 'circle' || (pad.shape === 'oval' && Math.abs(w - h) < 1e-9)) {
    g.flash(g.circle(Math.max(w0, h0) + 2 * grow), pad.pos);
  } else if (pad.shape === 'rect' && isRight(pad.rot)) {
    g.flash(`R,${size(w)}X${size(h)}`, pad.pos);
  } else if (pad.shape === 'oval' && isRight(pad.rot)) {
    g.flash(`O,${size(w)}X${size(h)}`, pad.pos);
  } else if (pad.shape === 'oval') {
    const s = padShape(pad, grow);
    g.region(capsulePoly(s.a, s.b, s.r));
  } else if (pad.shape === 'roundrect') {
    g.region(roundRectPoly(pad.pos, pad.size, pad.rot, grow));
  } else {
    g.region(rectCorners(pad.pos, pad.size, pad.rot, grow));
  }
}

/** Décalage simple d'un polygone (d < 0 = vers l'intérieur) par intersection des côtés décalés. */
export function offsetPolygon(pts, d) {
  const n = pts.length;
  if (!d || n < 3) return pts.map((p) => [...p]);
  const ccw = polyArea(pts) > 0;
  const lines = [];
  for (let i = 0; i < n; i++) {
    const a = pts[i], b = pts[(i + 1) % n];
    const len = Math.hypot(b[0] - a[0], b[1] - a[1]);
    if (len < 1e-9) continue;
    // Normale extérieure : à droite du sens de parcours pour un polygone trigonométrique.
    let nx = (b[1] - a[1]) / len, ny = -(b[0] - a[0]) / len;
    if (!ccw) { nx = -nx; ny = -ny; }
    lines.push({ a: [a[0] + nx * d, a[1] + ny * d], b: [b[0] + nx * d, b[1] + ny * d] });
  }
  const out = [];
  for (let i = 0; i < lines.length; i++) {
    const L1 = lines[(i + lines.length - 1) % lines.length], L2 = lines[i];
    const r = [L1.b[0] - L1.a[0], L1.b[1] - L1.a[1]], s = [L2.b[0] - L2.a[0], L2.b[1] - L2.a[1]];
    const den = r[0] * s[1] - r[1] * s[0];
    if (Math.abs(den) < 1e-9) { out.push(L2.a); continue; }
    const t = ((L2.a[0] - L1.a[0]) * s[1] - (L2.a[1] - L1.a[1]) * s[0]) / den;
    out.push([L1.a[0] + r[0] * t, L1.a[1] + r[1] * t]);
  }
  return out;
}

// ── Contexte commun ────────────────────────────────────────────

function context(project, lookup) {
  const pads = [];
  const comps = [];
  for (const c of project.components) {
    const fp = lookup(c.footprint);
    if (!fp) continue;
    comps.push({ c, fp });
    for (const p of worldPads(c, fp, copperLayers(project.board))) pads.push(p);
  }
  return { pads, comps, netOf: padNetMap(project) };
}

const LAYER_NAME = { F: 'Top', B: 'Bot' };

/** Zone interdite d'empreinte : « F et B » vaut toutes les couches cuivre (internes comprises). */
export function keepoutLayers(c, k, cu) {
  const kl = c.side === 'bottom' ? k.layers.map((l) => (l === 'F' ? 'B' : 'F')) : k.layers;
  return kl.includes('F') && kl.includes('B') ? cu : kl;
}

// ── Couches ────────────────────────────────────────────────────

export function copperLayer(project, lookup, layer, ctx = context(project, lookup)) {
  // Fonction X2 : Copper,L<n>,Top|Inr|Bot (n = rang dans l'empilage, 1 = dessus).
  const g = new GerberWriter(`Copper,L${layerNumber(project.board, layer)},${isOuter(layer) ? LAYER_NAME[layer] : 'Inr'}`);
  const cu = copperLayers(project.board);
  const onLayer = ctx.pads.filter((p) => p.layers.includes(layer));

  for (const z of project.zones.filter((x) => x.layer === layer)) {
    const clr = z.clearance ?? project.rules.clearance + 0.1;
    const area = z.outline ?? offsetPolygon(project.board.outline, -Math.max(project.rules.edgeClearance, 0.01));
    g.dark(true);
    g.region(area);
    g.dark(false);
    for (const p of onLayer) if (ctx.netOf.get(p.key) !== z.net) drawPad(g, p, clr);
    for (const p of ctx.pads) if (p.drill && !p.plated) g.flash(g.circle(p.drill + 2 * clr), p.pos);
    for (const t of project.tracks) if (t.layer === layer && t.net !== z.net) g.stroke(t.width + 2 * clr, t.pts);
    for (const v of project.vias) if (v.net !== z.net) g.flash(g.circle(v.diameter + 2 * clr), v.pos);
    for (const { c, fp } of ctx.comps) {
      for (const k of fp.keepouts ?? []) {
        if (!keepoutLayers(c, k, cu).includes(layer)) continue;
        const [x0, y0] = k.min, [x1, y1] = k.max;
        g.region([[x0, y0], [x1, y0], [x1, y1], [x0, y1]].map((p) => placePoint(c, p)));
      }
    }
  }

  g.dark(true);
  for (const t of project.tracks) if (t.layer === layer) g.stroke(t.width, t.pts);
  for (const p of onLayer) drawPad(g, p);
  for (const v of project.vias) g.flash(g.circle(v.diameter), v.pos);
  return g.toString();
}

/** Masque de soudure : ouvertures (fichier en polarité négative, convention X2). Vias recouverts. */
export function maskLayer(project, lookup, layer, ctx = context(project, lookup)) {
  const g = new GerberWriter(`Soldermask,${LAYER_NAME[layer]}`, 'Negative');
  for (const p of ctx.pads) {
    if (p.layers.includes(layer)) drawPad(g, p, MASK_MARGIN);
    else if (p.drill && !p.plated) g.flash(g.circle(p.drill + 2 * MASK_MARGIN), p.pos);
  }
  return g.toString();
}

/** Pâte à braser : pastilles CMS de la face. */
export function pasteLayer(project, lookup, layer, ctx = context(project, lookup)) {
  const g = new GerberWriter(`Paste,${LAYER_NAME[layer]}`);
  for (const p of ctx.pads) if (!p.drill && p.paste && p.layers.includes(layer)) drawPad(g, p);
  return g.toString();
}

function circlePts(c, r, n = 24) {
  const pts = [];
  for (let k = 0; k <= n; k++) pts.push([c[0] + r * Math.cos((2 * Math.PI * k) / n), c[1] + r * Math.sin((2 * Math.PI * k) / n)]);
  return pts;
}

/** Sérigraphie : graphismes d'empreintes, repères, textes ; rien sur les pastilles. */
export function silkLayer(project, lookup, layer, ctx = context(project, lookup)) {
  const g = new GerberWriter(`Legend,${LAYER_NAME[layer]}`);
  const avoid = ctx.pads.filter((p) => p.layers.includes(layer) || p.drill);

  const hitsPad = (p, w) => {
    const probe = { kind: 'cap', a: p, b: p, r: 0 };
    return avoid.some((pad) => shapeDist(probe, padShape(pad, SILK_PAD_GAP + w / 2)) <= 0);
  };
  /** Trace une polyligne en omettant les tronçons (≤ 0,3 mm) qui touchent une pastille. */
  const draw = (pts, w) => {
    let run = [];
    const flush = () => { if (run.length >= 2) g.stroke(w, run); run = []; };
    for (let i = 1; i < pts.length; i++) {
      const A = pts[i - 1], B = pts[i];
      const n = Math.max(1, Math.ceil(Math.hypot(B[0] - A[0], B[1] - A[1]) / 0.3));
      for (let k = 0; k < n; k++) {
        const a = [A[0] + ((B[0] - A[0]) * k) / n, A[1] + ((B[1] - A[1]) * k) / n];
        const b = [A[0] + ((B[0] - A[0]) * (k + 1)) / n, A[1] + ((B[1] - A[1]) * (k + 1)) / n];
        if (hitsPad(a, w) || hitsPad(b, w)) { flush(); continue; }
        if (!run.length) run.push(a);
        run.push(b);
      }
    }
    flush();
  };
  const text = (str, opts) => {
    const w = strokeWidth(opts.size);
    for (const line of strokeText(str, opts).lines) {
      if (line.length === 1 || (line.length === 2 && line[0][0] === line[1][0] && line[0][1] === line[1][1])) continue;
      draw(line, w);
    }
  };

  for (const { c, fp } of ctx.comps) {
    const side = c.side === 'bottom' ? 'B' : 'F';
    if (side !== layer) continue;
    const P = (p) => placePoint(c, p);
    for (const gr of fp.silk ?? []) {
      const w = gr.width ?? 0.15;
      if (gr.kind === 'line' || gr.kind === 'poly') {
        const pts = gr.pts.map(P);
        if (gr.fill && gr.kind === 'poly' && !pts.some((p) => hitsPad(p, 0))) g.region(pts);
        else draw(pts, w);
      } else if (gr.kind === 'rect' && gr.pts.length >= 2) {
        const [[x0, y0], [x1, y1]] = gr.pts;
        draw([[x0, y0], [x1, y0], [x1, y1], [x0, y1], [x0, y0]].map(P), w);
      } else if (gr.kind === 'circle' && gr.pts.length) {
        const pts = circlePts(P(gr.pts[0]), gr.r ?? 0.5);
        if (gr.fill) { if (!pts.some((p) => hitsPad(p, 0))) g.region(pts); } else draw(pts, w);
      }
    }
    // Repère au-dessus de l'encombrement, toujours lisible (rotation 0).
    const cy = courtyardPoly(c, fp);
    if (cy) {
      const [x0, , x1, y1] = polyBox(cy);
      text(c.ref, { pos: [(x0 + x1) / 2, y1 + 0.8], size: 1, mirror: layer === 'B' });
    }
  }
  for (const t of project.texts) {
    if (t.layer !== layer) continue;
    text(t.text, { pos: t.pos, size: t.size, rot: t.rot, mirror: layer === 'B' });
  }
  return g.toString();
}

/** Contour de découpe. */
export function edgeLayer(project) {
  const g = new GerberWriter('Profile,NP');
  g.stroke(0.1, project.board.outline, true);
  return g.toString();
}

/** Toutes les couches Gerber : { F_Cu, In1_Cu…In4_Cu (multicouche), B_Cu, F_Mask, B_Mask, F_Paste,
 *  B_Paste, F_Silkscreen, B_Silkscreen, Edge_Cuts }. */
export function gerberLayers(project, lookup) {
  const ctx = context(project, lookup);
  const inner = Object.fromEntries(copperLayers(project.board).filter((L) => !isOuter(L)).map((L) => [`${L}_Cu`, copperLayer(project, lookup, L, ctx)]));
  return {
    F_Cu: copperLayer(project, lookup, 'F', ctx),
    ...inner,
    B_Cu: copperLayer(project, lookup, 'B', ctx),
    F_Mask: maskLayer(project, lookup, 'F', ctx),
    B_Mask: maskLayer(project, lookup, 'B', ctx),
    F_Paste: pasteLayer(project, lookup, 'F', ctx),
    B_Paste: pasteLayer(project, lookup, 'B', ctx),
    F_Silkscreen: silkLayer(project, lookup, 'F', ctx),
    B_Silkscreen: silkLayer(project, lookup, 'B', ctx),
    Edge_Cuts: edgeLayer(project),
  };
}
